/**
 * MVP-491: the guest surface, driven by a keyboard alone.
 *
 * The room-based venue puts a PC with a keyboard in every room (the same fact MVP-490's `/` hotkey
 * was built on), and some guests use one because a phone touchscreen is not an option for them.
 * Two things stood between such a guest and a finished request:
 *
 *  1. The result list could be walked with `Tab` but not with `Up`/`Down`, which is what anyone
 *     expects of a list of results and what the console side already gives the operator.
 *  2. After the request went in, the queue-position card that says what happened was not reachable
 *     at all: the request sheet's focus trap returns focus to the row that opened it
 *     (`ui/useFocusTrap.ts:86`), so the guest lands back in the list with no way to find out that
 *     anything was queued. That is the ticket's own headline criterion failing.
 *
 * Both are the same problem — "which element should hold focus now" — so both resolve to one
 * intent type applied by one function.
 *
 * ## Why this file is split in two
 *
 * `resolve*` is arithmetic over facts the caller reads; `applyGuestNavIntent` is the only part that
 * touches an element, and it reaches those elements through an injected `targets` object rather
 * than through `document`. MVP-490's audit is the reason: five of its assertions stayed green
 * against handlers that were actually broken, because the pure predicate was tested with facts the
 * TEST handed in and nothing pinned that the component handed in the right ones. Recorders let the
 * test assert the effect — which element received focus, with which scroll behaviour, in which
 * order — instead of a `readFileSync` regex hoping the call site is right.
 *
 * ## What was deliberately NOT done
 *
 * No roving tabindex. It is the usual pattern for a listbox, and it works by taking every row but
 * one out of the tab order — which would delete the `Tab` path guests already have in exchange for
 * the arrow path they did not. Arrows are ADDED alongside `Tab`; an accelerator must never become
 * the only way in.
 *
 * No second `Escape` handler and no second focus trap. `ui/Sheet.tsx:44` already runs
 * `useFocusTrap`, which handles `Escape` (`:58`), traps `Tab` on the capture phase (`:75`) and
 * restores focus on close (`:27`/`:86`). A second one of either would close the sheet twice or
 * fight the first over `Tab`.
 */

/** The parts of a keydown this decision reads. No DOM types, no event object. */
export interface GuestNavKey {
  key: string;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}

/** What the request page looks like at the moment the key arrived. */
export interface GuestResultsNavContext {
  /** How many rows the result list is currently rendering. */
  count: number;
  /** Index of the row that holds focus, or -1 when focus is anywhere else. */
  activeIndex: number;
  /** Focus is in the guest search field specifically. */
  isSearchFieldFocused: boolean;
  /**
   * Focus is in some field the guest is typing into — or in a `<select>`, which owns Up/Down as
   * surely as an `<input>` owns a letter. Read with `lib/isTypingTarget.ts`, the one definition the
   * whole product shares since MVP-496 (the guest surface used to hand-roll its own, which had
   * drifted to omit `SELECT` — the exact tag this handler's arrows would have stolen).
   */
  isTypingTarget: boolean;
  /** A `[role="dialog"]` is up. The sheet owns every key while it is open. */
  isDialogOpen: boolean;
  /** The search field is on screen at all — it is not, in code mode. */
  hasSearchField: boolean;
}

/** Where focus should go, or that nothing should happen. */
export type GuestNavIntent =
  | { kind: 'none' }
  | { kind: 'focusRow'; index: number }
  | { kind: 'focusSearch' }
  | { kind: 'focusQueueCard' };

const NONE: GuestNavIntent = { kind: 'none' };

/**
 * Gap 1. Which row (if any) `Up`/`Down`/`Home`/`End` should move to.
 *
 * The rules that are decisions rather than plumbing:
 *
 * - **Focus must already be in the list or in the search field.** Arrows pressed with focus on the
 *   body are the ONLY way a keyboard-only guest scrolls the page; hijacking them document-wide
 *   would trade one gap for a worse one. `/` (MVP-490) is how you get into the surface; this is how
 *   you move once you are there.
 * - **`Down` from the search field enters the list at row 0.** In a single-line `<input>` the key
 *   would otherwise only move the caret to the end, which costs nothing to give up, and stepping
 *   from the query into its results is the motion the guest is already imagining.
 * - **`Up` from row 0 goes back to the search field**, so the path in and the path out are the same
 *   key. Only when there is a field to go back to — code mode has a pad instead.
 * - **`Home`/`End` only apply inside the list.** In a text field they are real editing keys.
 * - **Ends do not wrap.** A list that silently jumps from the last row to the first is how a guest
 *   who cannot see it loses track of where they are; stopping at the end is the announcement.
 */
export function resolveGuestResultsNav(ev: GuestNavKey, ctx: GuestResultsNavContext): GuestNavIntent {
  // Ctrl/Alt/Meta are the browser's and the OS's own chords (history, word-jump, Spaces). Shift is
  // read too and rejected here rather than ignored: Shift+Arrow in a field is a text selection.
  if (ev.ctrlKey || ev.altKey || ev.metaKey || ev.shiftKey) return NONE;
  if (ctx.isDialogOpen) return NONE;
  if (ctx.count <= 0) return NONE;

  const onRow = ctx.activeIndex >= 0 && ctx.activeIndex < ctx.count;

  // A field the guest is typing into keeps its arrows, with exactly one exception: `Down` in the
  // search field, handled below. The check is not redundant with `isSearchFieldFocused` — this
  // handler is bound to the whole `.guest-search` subtree, so any other field that ever lands in
  // there (or a contenteditable) is refused by default rather than by having been remembered.
  if (ctx.isTypingTarget && !ctx.isSearchFieldFocused) return NONE;

  if (!onRow) {
    if (ctx.isSearchFieldFocused && ev.key === 'ArrowDown') return { kind: 'focusRow', index: 0 };
    return NONE;
  }

  switch (ev.key) {
    case 'ArrowDown':
      return { kind: 'focusRow', index: Math.min(ctx.activeIndex + 1, ctx.count - 1) };
    case 'ArrowUp':
      if (ctx.activeIndex === 0) return ctx.hasSearchField ? { kind: 'focusSearch' } : NONE;
      return { kind: 'focusRow', index: ctx.activeIndex - 1 };
    case 'Home':
      return { kind: 'focusRow', index: 0 };
    case 'End':
      return { kind: 'focusRow', index: ctx.count - 1 };
    default:
      return NONE;
  }
}

/** What the page looks like when a submitted request has just come back. */
export interface QueueCardFocusContext {
  /** A submit succeeded and has not yet been honoured — see `RequestPage`'s pending ref. */
  pending: boolean;
  /** The queue-position card is rendered in this commit. */
  hasQueueCard: boolean;
  /** A `[role="dialog"]` is still up. */
  isDialogOpen: boolean;
}

/**
 * Gap 4. Whether focus should move into the queue-position card.
 *
 * Gated on `pending` rather than on "an `activeRequest` appeared", because `activeRequest` is also
 * set by the page-load `myRequests()` fetch, by `request.mine` SSE frames and by the 15s poll —
 * focusing on any of those would yank a guest out of the search field they are typing in, seconds
 * after arriving. Only a submit the guest just made earns the move.
 *
 * `isDialogOpen` is checked even though the success path closes the sheet in the same batch: the
 * duplicate-request branch (`RequestPage.handleSubmit`'s 409) leaves the sheet OPEN with an error
 * in it while still setting `activeRequest`, and pulling focus out from under a trap that is about
 * to restore it is the fight this whole module exists to avoid.
 */
export function resolveQueueCardFocus(ctx: QueueCardFocusContext): GuestNavIntent {
  if (!ctx.pending || !ctx.hasQueueCard || ctx.isDialogOpen) return NONE;
  return { kind: 'focusQueueCard' };
}

/** The one capability `applyGuestNavIntent` needs. `HTMLElement` satisfies it; so does a recorder. */
export interface GuestNavElement {
  focus(options?: FocusOptions): void;
  scrollIntoView(options?: ScrollIntoViewOptions): void;
}

/** The elements an intent can name. Injected, so a test can hand in recorders. */
export interface GuestNavTargets {
  rows: readonly GuestNavElement[];
  searchField: GuestNavElement | null;
  queueCard: GuestNavElement | null;
}

/**
 * Carry out an intent. Returns the element it moved to, or `null` if the intent named something
 * that is not there (a row index that has since gone, a card that never rendered) — the caller uses
 * that to decide whether to `preventDefault`.
 *
 * Always `focus({ preventScroll: true })` followed by an explicit
 * `scrollIntoView({ block: 'nearest' })`, never a bare `.focus()`. Same reason `useFocusTrap.ts:86`
 * gives: the browser's default focus scrolling walks every scrollable ancestor including
 * `document`/`body`, which `base.css` marks `overflow: hidden` but which can still be scrolled
 * programmatically — on the guest page that shows up as the whole layout shifting under a row.
 * `'nearest'` scrolls the list only as far as it must, so a guest holding `Down` walks the list
 * instead of the list jumping a screen per keypress.
 */
export function applyGuestNavIntent(intent: GuestNavIntent, targets: GuestNavTargets): GuestNavElement | null {
  const target = pickTarget(intent, targets);
  if (!target) return null;
  target.focus({ preventScroll: true });
  target.scrollIntoView({ block: 'nearest' });
  return target;
}

function pickTarget(intent: GuestNavIntent, targets: GuestNavTargets): GuestNavElement | null {
  switch (intent.kind) {
    case 'focusRow':
      return targets.rows[intent.index] ?? null;
    case 'focusSearch':
      return targets.searchField;
    case 'focusQueueCard':
      return targets.queueCard;
    case 'none':
      return null;
  }
}
